import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { DocumentViewer } from "@/components/ui/document-viewer";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { ProcessedDocument } from "./useProcessedDocumentsForEncounter";

interface ProcessedDocumentTabsProps {
  documents: ProcessedDocument[];
  isLoading: boolean;
  error: Error | null;
  failedCount?: number;
}

function typeLabel(fileIdentifier: ProcessedDocument["fileIdentifier"]): string {
  if (fileIdentifier === "endoscopy_procedure") return "Procedure";
  if (fileIdentifier === "endoscopy_histology") return "Histology";
  return "Codeable Summary";
}

const renderErrorFallback = (
  <div className="text-center text-destructive py-8">This document could not be displayed.</div>
);

export function ProcessedDocumentTabs({ documents, isLoading, error, failedCount = 0 }: ProcessedDocumentTabsProps) {
  const [activeTab, setActiveTab] = useState(documents[0]?.id ?? "");

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading processed documents…</div>;
  }

  if (error) {
    return <div className="text-destructive">{error.message}</div>;
  }

  const failureBanner = failedCount > 0 && (
    <div className="text-sm text-destructive mb-2">
      {failedCount} document{failedCount === 1 ? "" : "s"} failed to load — try refreshing.
    </div>
  );

  if (documents.length === 0) {
    return (
      <div>
        {failureBanner}
        {failedCount === 0 && (
          <div className="text-sm text-muted-foreground">
            No processed procedure/histology/codeable summary documents
          </div>
        )}
      </div>
    );
  }

  const selectedId = documents.some((doc) => doc.id === activeTab) ? activeTab : documents[0].id;

  return (
    <div>
      {failureBanner}
      <Tabs value={selectedId} onValueChange={setActiveTab} className="w-full">
        <TabsList className="flex h-auto w-full flex-wrap gap-1">
          {documents.map((doc) => (
            <TabsTrigger key={doc.id} value={doc.id} className="text-xs whitespace-nowrap gap-1">
              <Badge variant="outline">{typeLabel(doc.fileIdentifier)}</Badge>
              {doc.fileDate.slice(0, 10)}
            </TabsTrigger>
          ))}
        </TabsList>

        {documents.map((doc) => (
          <TabsContent key={doc.id} value={doc.id} className="border rounded-lg bg-white p-4">
            <ErrorBoundary fallback={renderErrorFallback}>
              <p className="text-sm font-medium mb-2">{doc.title}</p>
              <DocumentViewer
                content={doc.text}
                className="text-sm"
                markdown={doc.fileIdentifier === "codeable_summary"}
              />
            </ErrorBoundary>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
